'use client'

import { useMultipleSelection, useCombobox } from 'downshift'
import { ChevronDown, X } from 'lucide-react'
import * as React from 'react'
import { useState } from 'react'

import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { cn } from '#app/utils/misc.tsx'

export interface MultipleComboBoxItem {
	id: string
	name: string
}

export interface MultipleComboBoxProps {
	items: MultipleComboBoxItem[]
	selectedItems?: MultipleComboBoxItem[]
	selectedIds?: string[]
	onSelectionChange?: (selectedItems: MultipleComboBoxItem[]) => void
	placeholder?: string
	className?: string
	disabled?: boolean
	maxSelected?: number
	searchPlaceholder?: string
}

export function MultipleComboBox({
	items,
	selectedItems = [],
	selectedIds = [],
	onSelectionChange,
	placeholder = 'Select items...',
	className,
	disabled = false,
	maxSelected,
	searchPlaceholder = 'Search...',
}: MultipleComboBoxProps) {
	const [inputValue, setInputValue] = useState('')

	// Convert selectedIds to selectedItems if provided
	const computedSelectedItems = React.useMemo(() => {
		if (selectedItems.length > 0) {
			return selectedItems
		}
		if (selectedIds.length > 0) {
			return items.filter(item => selectedIds.includes(item.id))
		}
		return []
	}, [selectedItems, selectedIds, items])

	// Filter items based on input value and exclude already selected items
	const filteredItems = React.useMemo(() => {
		return items.filter(
			item =>
				item.name.toLowerCase().includes(inputValue.toLowerCase()) &&
				!computedSelectedItems.some(
					selectedItem => selectedItem.id === item.id,
				),
		)
	}, [items, inputValue, computedSelectedItems])

	const {
		getSelectedItemProps,
		getDropdownProps,
		addSelectedItem,
		removeSelectedItem,
		selectedItems: internalSelectedItems,
	} = useMultipleSelection({
		selectedItems: computedSelectedItems,
		onSelectedItemsChange: ({ selectedItems: newSelectedItems }) => {
			onSelectionChange?.(newSelectedItems || [])
		},
	})

	const {
		isOpen,
		getToggleButtonProps,
		getMenuProps,
		getInputProps,
		highlightedIndex,
		getItemProps,
		openMenu,
	} = useCombobox({
		items: filteredItems,
		itemToString: item => (item ? item.name : ''),
		defaultHighlightedIndex: undefined,
		selectedItem: null,
		inputValue,
		stateReducer: (state, actionAndChanges) => {
			const { changes, type } = actionAndChanges
			switch (type) {
				case useCombobox.stateChangeTypes.InputKeyDownEnter:
				case useCombobox.stateChangeTypes.ItemClick:
					return {
						...changes,
						isOpen: true, // keep the menu open after selection
						highlightedIndex: undefined, // with the first option highlighted
					}
				default:
					return changes
			}
		},
		onStateChange: ({
			inputValue: newInputValue,
			type,
			selectedItem: newSelectedItem,
		}) => {
			switch (type) {
				case useCombobox.stateChangeTypes.InputKeyDownEnter:
				case useCombobox.stateChangeTypes.ItemClick:
				case useCombobox.stateChangeTypes.InputBlur:
					if (newSelectedItem) {
						setInputValue('')
						addSelectedItem(newSelectedItem)
					}
					break
				case useCombobox.stateChangeTypes.InputChange:
					setInputValue(newInputValue || '')
					break
				default:
					break
			}
		},
	})

	const canAddMore = !maxSelected || internalSelectedItems.length < maxSelected

	return (
		<div className={cn('relative', className)}>
			<div
				className={cn(
					'border-input bg-background ring-offset-background focus-within:ring-ring flex min-h-10 w-full flex-wrap items-center gap-1 rounded-md border px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-offset-2',
					disabled && 'cursor-not-allowed opacity-50',
					!disabled && canAddMore && 'cursor-pointer',
				)}
				{...getToggleButtonProps({
					disabled: disabled || !canAddMore,
				})}
			>
				{/* Selected items as badges */}
				{internalSelectedItems.map((selectedItem, index) => (
					<div
						key={selectedItem.id}
						{...getSelectedItemProps({
							selectedItem,
							index,
						})}
					>
						<Badge variant="brand" className="gap-1">
							{selectedItem.name}
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="h-4 w-4 p-0 hover:bg-brand-cyan-10"
								onClick={e => {
									e.stopPropagation()
									removeSelectedItem(selectedItem)
								}}
								disabled={disabled}
							>
								<X className="h-3 w-3" />
							</Button>
						</Badge>
					</div>
				))}

				{/* Input field */}
				<input
					{...getInputProps(
						getDropdownProps({
							preventKeyAction: isOpen,
							disabled,
							placeholder:
								internalSelectedItems.length === 0
									? placeholder
									: canAddMore
										? searchPlaceholder
										: '',
							className:
								'flex-1 bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed text-base',
							onFocus: () => {
								if (!disabled && canAddMore && !isOpen) {
									openMenu()
								}
							},
						}),
					)}
				/>

				{/* Toggle button */}
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="h-6 w-6 p-0 hover:bg-transparent"
					disabled={disabled || !canAddMore}
				>
					<ChevronDown
						className={cn(
							'h-4 w-4 transition-transform',
							isOpen && 'rotate-180',
						)}
					/>
				</Button>
			</div>

			{/* Dropdown menu */}
			<div
				className={cn(
					'absolute top-full z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-white p-1 shadow-md',
					(!isOpen || !canAddMore) && 'hidden',
				)}
				{...getMenuProps()}
			>
				{filteredItems.length > 0 ? (
					filteredItems.map((item, index) => (
						<div
							key={item.id}
							className={cn(
								'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
								highlightedIndex === index
									? 'text-accent-foreground bg-slate-200'
									: 'hover:text-accent-foreground hover:bg-slate-200',
							)}
							{...getItemProps({ item, index })}
						>
							{item.name}
						</div>
					))
				) : (
					<div className="text-muted-foreground py-6 text-center text-sm">
						{inputValue ? 'No results found.' : 'No items available.'}
					</div>
				)}
			</div>

			{/* Max selected message */}
			{maxSelected && internalSelectedItems.length >= maxSelected && (
				<div className="text-muted-foreground mt-1 text-xs">
					Maximum {maxSelected} items selected
				</div>
			)}
		</div>
	)
}
